import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Link, useNavigate, useParams } from "react-router-dom";
import theme from "../../theme";
import useButtonStyles from "../../styles/useButtonStyles";
import { useState, useEffect } from "react";
import SelectInputField from "../forms/formElements/SelectInputField";
import BasicTextField from "../forms/formElements/BasicTextField";
import { constant } from "../../constants";
import { APIConstants } from "../../constants/ApiConstants";
import axios from "axios";
import { useDispatch } from "react-redux";
import { adminActions } from "../../redux/slices/adminSlice";
import { useAppSelector } from "../../redux/hooks";
import { AddAdminFormDataType } from "./AddUser";
import { roleActions } from "../../redux/slices/roleSlice";
import BasicModal from "../ui/BasicModal";
import Loader from "../ui/Loader";
import { filterConstants } from "../../constants/AppConstants";

export function ViewUserProfile() {
  const params = useParams();
  const { userId } = params;

  const buttonStyles = useButtonStyles();
  const [adminFormState, setAdminFormState] = useState<AddAdminFormDataType>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [noChangesMade, setNoChangesMade] = useState<boolean>(false);
  const matchEmail = localStorage.getItem("username");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const email = window.location.search.replace("?", "");
  const singleAdminList = useAppSelector(
    (state: any) => state.persistReducers.getAdminList.adminListDetails
  );
  const roleList = useAppSelector(
    (state: any) => state.persistReducers.getRoleList.roleList
  );

  useEffect(() => {
    setAdminFormState(singleAdminList);
  }, [singleAdminList]);
  const sendUpdateEmail = async () => {
    try {
      const changes = [];

      if (adminFormState?.firstName !== singleAdminList?.firstName) {
        changes.push(
          `First Name: ${singleAdminList?.firstName} -> ${adminFormState?.firstName}`
        );
      }

      if (adminFormState?.lastName !== singleAdminList?.lastName) {
        changes.push(
          `Last Name: ${singleAdminList?.lastName} -> ${adminFormState?.lastName}`
        );
      }

      if (adminFormState?.user_role !== singleAdminList?.user_role) {
        changes.push(
          `User Role: ${singleAdminList?.user_role} -> ${adminFormState?.user_role}`
        );
      }

      if (adminFormState?.department !== singleAdminList?.department) {
        changes.push(
          `Department: ${singleAdminList?.department} -> ${adminFormState?.department}`
        );
      }

      if (adminFormState?.status !== singleAdminList?.status) {
        changes.push(
          `Status: ${singleAdminList?.status} -> ${adminFormState?.status}`
        );
      }

      if (changes.length === 0) {
        setNoChangesMade(true);
        return;
      }

      const changeMessage = changes.join("<br><br>");

      const emailData = {
        email: adminFormState?.email,
        subject: "Profile Update",
        body: `
          <div style="margin: 10px; padding: 10px; line-height: 1.5;">
            Your profile information has been updated.<br><br>
            <strong>Changes:</strong><br><br>
            ${changeMessage}
          </div>
        `,
        fileType: "text",
      };

      const emailResponse = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.sendEmail,
        emailData
      );
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  useEffect(() => {
    const fetchRoleList = async () => {
      try {
        roleList.length > 0 ? setLoading(false) : setLoading(true);
        const response = await axios.get(
          process.env.REACT_APP_API_URL + APIConstants.role + "?type=Admin"
        );
        const data = response.data.data;

        const roleListData = data.map((item: any) => {
          return {
            name: item.name ? item.name : "-",
            description: item.description ? item.description : "-",
          };
        });

        const roles = data.map((item: any) => {
          return item.name;
        });
        setRoles(roles);
        dispatch(roleActions.updateRoleList({ roleList: roleListData }));
        setLoading(false);
        setUploadError(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
        setUploadError(false);
      }
    };

    fetchRoleList();
  }, []);

  useEffect(() => {
    const fetchSingleUser = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `${
            process.env.REACT_APP_API_URL +
            APIConstants.admin +
            "?email=" +
            userId
          }`
        );
        const data = response.data.result[0];
        dispatch(
          adminActions.updateAdminListDetails({
            adminListDetails: data,
          })
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchSingleUser();
  }, []);

  const deleteUser = async () => {
    try {
      setLoading(true);
      await axios.delete(
        process.env.REACT_APP_API_URL + APIConstants.admin + "?email=" + userId
      );
      setUploadError(false);
      navigate("/admin");
    } catch (error) {
      setUploadError(true);
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (password.trim() !== "" && confirmPassword.trim() === "") {
      setPasswordError(true);
      return;
    }

    if (password.trim() !== confirmPassword.trim()) {
      setPasswordError(true);
      return;
    }
    await updateUser();
  };

  const updateUser = async () => {
    try {
      setLoading(true);
      const data = {
        email: adminFormState?.email,
        firstName: adminFormState?.firstName,
        lastName: adminFormState?.lastName,
        department: adminFormState?.department,
        user_role: adminFormState?.user_role,
        status: adminFormState?.status,
      };
      await axios.patch(
        process.env.REACT_APP_API_URL + APIConstants.admin,
        data
      );
      await sendUpdateEmail();
      setUploadError(false);
    } catch (error) {
      setUploadError(true);
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };
  useEffect(() => {
    if (password.trim() !== "") {
      setPasswordError(false);
    } else {
      setPasswordError(false);
      setConfirmPassword("");
    }
  }, [password]);

  return (
    <Grid>
      <Grid
        item
        width={"100%"}
        height={"6.25rem"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        borderBottom={`${theme?.border?.xsmall}${theme.color?.dividerLight}`}
        mb={4}
        sx={{ background: theme.color?.background }}
      >
        <Grid display={"flex"} alignItems={"center"} px={4} gap={2}>
          <Link to="/admin">
            <ArrowBackIosIcon />
          </Link>{" "}
          <Typography
            variant="h6"
            sx={{
              color: theme?.color?.primary,
              fontFamily: theme.font?.fontFamily.primary,
              fontWeight: "600",
            }}
          >
            {singleAdminList.firstName} {singleAdminList.lastName}
          </Typography>
        </Grid>
        <Grid display={"flex"} justifyContent={"end"} mr={4}>
          <Button
            onClick={handleOpen}
            variant="text"
            color="error"
            sx={{ ...buttonStyles.outlinedDanger }}
          >
            Delete User
          </Button>
        </Grid>
      </Grid>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            fontFamily: theme.font?.fontFamily.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        ></DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ fontFamily: theme.font?.fontFamily.primary }}
          >
            Are you sure you want to delete this user ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          {/* {loadingAPI ? (
            <CircularProgress size={24} />
          ) : ( */}
          <>
            <Button
              sx={{ ...buttonStyles.outlinedSecondary }}
              onClick={handleClose}
              autoFocus
            >
              Cancel
            </Button>
            <Button
              sx={{ ...buttonStyles.containedSecondary }}
              onClick={async () => {
                await deleteUser();
              }}
            >
              OK
            </Button>
          </>
          {/* )} */}
        </DialogActions>
      </Dialog>
      <Grid>
        <Grid ml={4}>
          <Typography
            fontWeight={"700"}
            fontFamily={theme.font?.fontFamily.primary}
          >
            Update Profile
          </Typography>
        </Grid>
        <Grid>
          <Grid
            sx={{
              backgroundColor: theme?.color?.background,
              borderRadius: "8px",
            }}
            padding={2}
            pl={4}
            pr={4}
            component={"form"}
            onSubmit={submitHandler}
            margin={3}
            alignSelf={"center"}
          >
            <Grid
              display={"flex"}
              justifyContent={"space-evenly"}
              alignItems={"end"}
              padding={2}
            >
              <Grid
                container
                display={"flex"}
                width={"100%"}
                alignItems={"end"}
              >
                <Grid item lg={3.5} md={3.5}>
                  <Typography
                    sx={{ fontFamily: theme.font?.fontFamily.primary }}
                  >
                    User Role *
                  </Typography>
                  <SelectInputField
                    sx={{ width: "95%" }}
                    setState={setAdminFormState}
                    stateKey="user_role"
                    defaultValue={
                      singleAdminList.user_role
                        ? singleAdminList.user_role
                        : filterConstants.Select
                    }
                    align="left"
                    options={roles}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              display={"flex"}
              alignItems={"flex-end"}
              width={"100%"}
              spacing={2}
              padding={2}
              marginBottom={2}
            >
              <Grid item md={3.5} lg={3.5}>
                <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                  First Name *
                </Typography>
                <BasicTextField
                  placeholder=""
                  stateKey="firstName"
                  defaultValue={singleAdminList?.firstName}
                  setState={setAdminFormState}
                  clear={false}
                />
              </Grid>
              <Grid item md={3.5} lg={3.5}>
                <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                  Last Name *
                </Typography>
                <BasicTextField
                  placeholder=""
                  stateKey="lastName"
                  defaultValue={singleAdminList?.lastName}
                  setState={setAdminFormState}
                  clear={false}
                />
              </Grid>
              <Grid item md={3.5} lg={3.5}>
                <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                  Department
                </Typography>
                <BasicTextField
                  placeholder=""
                  stateKey="department"
                  defaultValue={singleAdminList?.department}
                  setState={setAdminFormState}
                  clear={false}
                />
              </Grid>
              <Grid item width={"29%"}>
                <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                  Email
                </Typography>
                <BasicTextField
                  placeholder=""
                  stateKey="email"
                  defaultValue={singleAdminList?.email}
                  setState={setAdminFormState}
                  clear={false}
                  disabled={true}
                />
              </Grid>
              <Grid item md={3.5} lg={3.5}>
                <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                  Status *
                </Typography>
                <SelectInputField
                  readOnly={matchEmail === singleAdminList?.email}
                  sx={{ width: "100%" }}
                  setState={setAdminFormState}
                  stateKey="status"
                  defaultValue={
                    singleAdminList.status ? singleAdminList.status : filterConstants.Select
                  }
                  align="left"
                  options={[
                    filterConstants.Select,
                    filterConstants.Active,
                    filterConstants.Inactive,
                  ]}
                />
              </Grid>
            </Grid>

            <Grid
              container
              marginTop={4}
              item
              justifyContent={"end"}
              padding={2}
              borderTop={`${theme?.border?.xsmall}${theme.color?.dividerLight}`}
              paddingTop={4}
            >
              <Button
                sx={{ ...buttonStyles.outlinedSecondary, width: "20%", mr: 2 }}
                variant="outlined"
                component={Link}
                to="/admin"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                sx={{ ...buttonStyles.containedSecondary, width: "20%" }}
                variant="contained"
              >
                Update
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {showModal &&
        (uploadError ? (
          <BasicModal
            title={"Something went wrong"}
            success={false}
            setState={setShowModal}
          />
        ) : (
          <BasicModal
            title={"Admin Updated Successfully!"}
            success={true}
            setState={setShowModal}
          />
        ))}
      {loading && <BasicModal content={<Loader />} />}
    </Grid>
  );
}
